import { parseQueryStringMulti } from '../../properties/property-view/utils/advancedFilterUtils';
import type {
  GlobalFilterMetadataArgs,
  GlobalOrderSearchArgs,
} from '../types/globalOrders.types';

/**
 * Serializes typed args into the /internal/orders query string. Unlike the
 * client list (pre-built filterQuery fragments), the internal endpoints get
 * a typed-args → URLSearchParams boundary so the contract is testable.
 *
 * Two invariants enforced here rather than left to a backend 400:
 *  - orderStatuses may only accompany view=ORDER_SEARCH (locked views lock
 *    server-side; combining is a 400).
 *  - The drawer QS's `address` key is dropped: the omnibox owns search.
 */
const appendScopeParams = (
  p: URLSearchParams,
  args: GlobalFilterMetadataArgs,
): void => {
  p.set('view', args.view);
  if (args.orderStatuses?.length) {
    if (args.view !== 'ORDER_SEARCH') {
      throw new Error(
        `orderStatuses cannot be combined with the locked view ${args.view}`,
      );
    }
    args.orderStatuses.forEach((s) => p.append('orderStatuses', s));
  }
  args.entityUUIDs?.forEach((u) => p.append('entityUUIDs', u));
  if (args.orderDateFrom) p.set('orderDateFrom', args.orderDateFrom);
  if (args.orderDateTo) p.set('orderDateTo', args.orderDateTo);
  if (args.moreFilters) {
    const advanced = parseQueryStringMulti(args.moreFilters);
    delete advanced.address;
    Object.entries(advanced).forEach(([key, values]) =>
      values.forEach((v) => p.append(key, v)),
    );
  }
};

export const buildGlobalOrderParams = (args: GlobalOrderSearchArgs): string => {
  const p = new URLSearchParams();
  appendScopeParams(p, args);
  const search = args.search?.trim();
  if (search) p.set('search', search);
  p.set('page', String(args.page));
  p.set('size', String(args.size));
  if (args.sortBy) p.set('sortBy', args.sortBy);
  if (args.sortDir) p.set('sortDir', args.sortDir);
  return p.toString();
};

export const buildGlobalMetadataParams = (
  args: GlobalFilterMetadataArgs,
): string => {
  const p = new URLSearchParams();
  appendScopeParams(p, args);
  return p.toString();
};
